<template>
  <div>
    <DeliveryItemsList />

    <div class="step__block-row">
      <p class="step__block-text">
        Delivery type
        <span v-if="showNotIncludedText">- not included</span>
      </p>
      <DeliverySelect v-model:showText="showNotIncludedText" />
    </div>
    <CombinedDeliveryCheckbox />

    <div class="step__block-total">
      <h3 class="step__block-title">page Total:</h3>
      <PriceValue
        v-if="totalDeliveryPrice"
        :price="totalDeliveryPrice"
        class="step__block-title"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch, ref } from "vue";

import {
  DeliveryItemsList,
  DeliverySelect,
  CombinedDeliveryCheckbox,
} from "@/modules/delivery";
import { Pricing } from "@/app/domain";
import { PriceValue } from "@/shared";

import { PricingService } from "@/app/services/pricing.service";
import { useDeliveryStore } from "@/stores/new/delivery.store";
import { useNewEstimateStore } from "@/stores/new/estimateStore";

defineExpose({ validatePage });

const deliveryStore = useDeliveryStore();
const estimateStore = useNewEstimateStore();
const pricingService = new PricingService();

const estimate = computed(() => estimateStore.estimate);
const dealType = computed(() => estimateStore.estimate.dealTypeCode);
const totalDeliveryPrice = computed(() => deliveryStore.totalDeliveryPrice);
const showNotIncludedText = ref(false);
function validatePage() {
  return null;
}

// calculate total delivery price
watch(
  () => [
    deliveryStore.deliveryItems,
    deliveryStore.deliveryType,
    deliveryStore.numberOfTracks,
  ],
  () => {
    const deliveryType = deliveryStore.deliveryType;

    const currentComponent = deliveryStore.deliveryItems.find(
      (item) => item.delivery_type === deliveryType,
    );

    const totalPrice = pricingService.calculateDeliveryPrice(currentComponent);

    const formattedTotalPrice = new Pricing(
      totalPrice.price,
      totalPrice.priceB2C,
      totalPrice.priceB2B,
    );

    deliveryStore.totalDeliveryPrice = formattedTotalPrice;
  },
  { deep: true },
);

watch(
  () => [estimateStore.estimate.delivery, deliveryStore.totalWeight],
  ([delivery, weight], prevValues) => {
    if (!delivery) {
      deliveryStore.deliveryType = "";
      deliveryStore.totalDeliveryPrice = new Pricing(0, 0, 0);
      return;
    }

    if (!deliveryStore.deliveryType || prevValues[1] !== weight) {
      deliveryStore.defineDeliveryType(+weight);
    }
  },
  {
    deep: true,
  },
);

watch(
  () => [deliveryStore.totalDeliveryPrice, estimateStore.estimate.delivery],
  ([currentDeliveryTotal, isDelivery]) => {
    const pageTotal = currentDeliveryTotal
      .getSystemPrice(dealType.value)
      .getValue();
    if (isDelivery) {
      estimateStore.estimate.deliverySum = pageTotal;
    } else {
      estimateStore.estimate.deliverySum = 0;
    }
  },
  {
    deep: true,
  },
);

watch(
  () => deliveryStore.totalGlassWeight,
  (glassWeight) => {
    const weight = parseInt(glassWeight).toFixed();
    estimate.value.totalGlassWeight = Number(weight);
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  deliveryStore.createDeliveryItems();
  deliveryStore.getOrderWeight();
});
</script>

<style scoped>
.step__block-text {
  font-family: Gilroy;
  font-weight: 400;
  font-size: 13px;
  line-height: 100%;
  color: rgba(5, 33, 65, 1);
}

.step__block-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.step__block-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-block: 20px;
  margin-top: 40px;

  border-top: 1px solid #d2d8e3;
  border-bottom: 1px solid #d2d8e3;
}

.step__block-title {
  font-family: Gilroy;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: uppercase;
  color: #052141;
}

.select {
  width: 30%;
}
</style>
